import React, { useEffect, useMemo, useState } from "react";

import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  MenuItem,
  TextField,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";

import {
  AttachMoney,
  ShoppingCart,
  TrendingUp,
  Paid,
} from "@mui/icons-material";

import { DataGrid } from "@mui/x-data-grid";

import { getCompletedOrders } from "../../services/source";

const months = [
  { value: 0, label: "Todos os Meses" },
  { value: 1, label: "Janeiro" },
  { value: 2, label: "Fevereiro" },
  { value: 3, label: "Março" },
  { value: 4, label: "Abril" },
  { value: 5, label: "Maio" },
  { value: 6, label: "Junho" },
  { value: 7, label: "Julho" },
  { value: 8, label: "Agosto" },
  { value: 9, label: "Setembro" },
  { value: 10, label: "Outubro" },
  { value: 11, label: "Novembro" },
  { value: 12, label: "Dezembro" },
];

const Vendas = () => {
  const currentYear = new Date().getFullYear();

  const [orders, setOrders] = useState([]);
  const [month, setMonth] = useState(0);

  const [year, setYear] = useState(currentYear);
  const [currency, setCurrency] = useState("Kz");

  const [openItems, setOpenItems] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const loadOrders = async () => {
    try {
      const response = await getCompletedOrders();
      setOrders(response.data.orders || []);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    loadOrders();
  }, []);

  const years = useMemo(() => {
    const unique = [
      ...new Set(orders.map((o) => new Date(o.created_at).getFullYear())),
    ];

    unique.sort((a, b) => b - a);

    return unique;
  }, [orders]);

  const getCurrency = (location) => {
    return location?.trim().toLowerCase().includes("lisboa") ? "€" : "Kz";
  };

  // const filteredOrders = useMemo(() => {
  //   return orders.filter((order) => {
  //     const date = new Date(order.created_at);

  //     const orderMonth = date.getMonth() + 1;
  //     const orderYear = date.getFullYear();

  //     const orderCurrency = getCurrency(order.location);

  //     return (
  //       (month === 0 || orderMonth === month) &&
  //       orderYear === year &&
  //       orderCurrency === currency
  //     );
  //   });
  // }, [orders, month, year, currency]);

  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      const date = new Date(order.created_at);

      const orderMonth = date.getMonth() + 1;
      const orderYear = date.getFullYear();

      const orderCurrency = getCurrency(order.items?.[0]?.location);

      return (
        (month === 0 || orderMonth === month) &&
        orderYear === year &&
        orderCurrency === currency
      );
    });
  }, [orders, month, year, currency]);

  const totalSales = filteredOrders.reduce(
    (sum, item) => sum + Number(item.total),
    0,
  );

  const average =
    filteredOrders.length > 0 ? totalSales / filteredOrders.length : 0;

  const biggestSale =
    filteredOrders.length > 0
      ? Math.max(...filteredOrders.map((x) => Number(x.total)))
      : 0;

  const columns = [
    {
      field: "order_number",
      headerName: "Pedido",
      flex: 1,

      renderCell: (params) => (
        <Typography
          color="primary"
          fontWeight="bold"
          sx={{
            cursor: "pointer",
          }}
          onClick={() => {
            setSelectedOrder(params.row);
            setOpenItems(true);
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: "customer_name",
      headerName: "Cliente",
      flex: 1,
    },
    {
      field: "payment_method",
      headerName: "Pagamento",
      flex: 1,
    },
    {
      field: "total",
      headerName: "Valor",
      width: 150,
      renderCell: (params) => {
        const location = params.row.items?.[0]?.location;

        return (
          <>
            {Number(params.value).toLocaleString()} {getCurrency(location)}
          </>
        );
      },
    },
    {
      field: "created_at",
      headerName: "Data",
      flex: 1,
      renderCell: (params) => new Date(params.value).toLocaleString("pt-PT"),
    },
  ];

  const SummaryCard = ({ title, value, icon, color }) => (
    <Card
      sx={{
        borderRadius: 4,
        height: "100%",
        boxShadow: 4,
      }}
    >
      <CardContent>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box>
            <Typography color="text.secondary" fontWeight="bold">
              {title}
            </Typography>

            <Typography variant="h5" fontWeight="bold" mt={1}>
              {value}
            </Typography>
          </Box>

          <Box
            sx={{
              bgcolor: color,
              p: 2,
              borderRadius: "50%",
              color: "white",
            }}
          >
            {icon}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );

  return (
    <Box
      sx={{
        maxWidth: 1500,
        mx: "auto",
        px: {
          xs: 2,
          md: 4,
        },
        pb: 10,
      }}
    >
      <Typography variant="h4" fontWeight="bold" mb={4}>
        Painel das Vendas
      </Typography>
      <Paper
        elevation={3}
        sx={{
          p: 3,
          borderRadius: 4,
          mb: 4,
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              select
              label="Mês"
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
            >
              {months.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {m.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              select
              label="Ano"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
            >
              {years.map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              select
              label="Moeda"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
            >
              <MenuItem value="Kz">Kz</MenuItem>

              <MenuItem value="€">€</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Paper>
      <Grid container spacing={3} mb={4} mt={1}>
        <Grid item xs={12} sm={6} lg={3}>
          <SummaryCard
            title="Total de Vendas"
            value={`${totalSales.toLocaleString()} ${currency}`}
            color="#2e7d32"
            icon={<AttachMoney />}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <SummaryCard
            title="Pedidos Concluídos"
            value={filteredOrders.length}
            color="#1565c0"
            icon={<ShoppingCart />}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <SummaryCard
            title="Venda Média"
            value={`${average.toLocaleString(undefined, {
              maximumFractionDigits: 2,
            })} ${currency}`}
            color="#ef6c00"
            icon={<TrendingUp />}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <SummaryCard
            title="Maior Venda"
            value={`${biggestSale.toLocaleString()} ${currency}`}
            color="#8e24aa"
            icon={<Paid />}
          />
        </Grid>
      </Grid>
      <Paper
        elevation={5}
        sx={{
          borderRadius: 4,
          overflow: "hidden",
          mt: 4,
        }}
      >
        <DataGrid
          rows={filteredOrders}
          columns={columns}
          autoHeight
          pageSizeOptions={[10, 25, 50]}
          disableRowSelectionOnClick
          sx={{
            border: 0,

            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#f5f5f5",
              fontWeight: "bold",
            },

            // Center header content
            "& .MuiDataGrid-columnHeader": {
              justifyContent: "center",
            },

            // Center header text
            "& .MuiDataGrid-columnHeaderTitle": {
              width: "100%",
              textAlign: "center",
              fontWeight: "bold",
            },

            // Center all cell contents
            "& .MuiDataGrid-cell": {
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
            },

            "& .MuiDataGrid-row:hover": {
              backgroundColor: "#fafafa",
            },
          }}
        />
      </Paper>
      <Dialog
        open={openItems}
        onClose={() => setOpenItems(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          Produtos Vendidos - {selectedOrder?.order_number}
        </DialogTitle>

        <DialogContent>
          {selectedOrder?.items?.map((item, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "center",
                mb: 2,
                p: 2,
                borderRadius: 2,
                boxShadow: 2,
              }}
            >
              <Box
                component="img"
                src={`${process.env.REACT_APP_API_URL}/${item.image}`}
                sx={{
                  width: 80,
                  height: 80,
                  objectFit: "cover",
                  borderRadius: 2,
                }}
              />

              <Box>
                <Typography fontWeight="bold">{item.title}</Typography>

                <Typography>Código: {item.product_code}</Typography>

                <Typography>Quantidade: {item.quantity}</Typography>

                <Typography>
                  Preço: {Number(item.price).toLocaleString()}{" "}
                  {getCurrency(item.location)}
                </Typography>

                <Typography fontWeight="bold">
                  Total:{" "}
                  {(
                    Number(item.price) * Number(item.quantity)
                  ).toLocaleString()}{" "}
                  {getCurrency(item.location)}
                </Typography>
              </Box>
            </Box>
          ))}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpenItems(false)}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>{" "}
    </Box>
  );
};

export default Vendas;
